import { z } from 'zod';
import { CountryCodeSchema, type CountryCode } from './geo';
import { OutletStatusSchema } from './restaurants';

/* ------------------------------------------------------------------ */
/* Menu + catalogue (W5)                                               */
/*                                                                     */
/* Every outlet has its own menu: categories → dishes and combos.      */
/* Dishes can have sizes (each with its own price) and add-on groups   */
/* (shared per outlet). Time slots limit when a category or dish is    */
/* on sale. Money is always in minor units (paise / paisa).            */
/*                                                                     */
/* Change rules: stock, descriptions, photos and small spelling fixes  */
/* go live at once. New dishes, price changes, renames, food-type and  */
/* combo changes wait for an Ops review; the old version stays live    */
/* until then.                                                         */
/* ------------------------------------------------------------------ */

/** Largest single price: ₹ / Rs 1,00,000. */
export const MAX_MENU_PRICE_MINOR = 100_000_00;
export const MenuMoneySchema = z
  .number()
  .int('Use whole paise / paisa')
  .min(0)
  .max(MAX_MENU_PRICE_MINOR, 'That price is too high');

export const MENU_FOOD_TYPES = ['veg', 'non_veg', 'egg'] as const;
export const MenuFoodTypeSchema = z.enum(MENU_FOOD_TYPES);
export type MenuFoodType = z.infer<typeof MenuFoodTypeSchema>;
export const MENU_FOOD_TYPE_LABELS: Record<MenuFoodType, string> = {
  veg: 'Veg',
  non_veg: 'Non-veg',
  egg: 'Contains egg',
};

export const SPICE_LEVELS = ['none', 'mild', 'medium', 'hot'] as const;
export const SpiceLevelSchema = z.enum(SPICE_LEVELS);
export type SpiceLevel = z.infer<typeof SpiceLevelSchema>;
export const SPICE_LEVEL_LABELS: Record<SpiceLevel, string> = {
  none: 'Not spicy',
  mild: 'Mild',
  medium: 'Medium',
  hot: 'Hot',
};

export const ALLERGENS = [
  'gluten',
  'dairy',
  'nuts',
  'peanuts',
  'soy',
  'egg',
  'fish',
  'shellfish',
  'sesame',
  'mustard',
] as const;
export const AllergenSchema = z.enum(ALLERGENS);
export type Allergen = z.infer<typeof AllergenSchema>;
export const ALLERGEN_LABELS: Record<Allergen, string> = {
  gluten: 'Gluten (wheat)',
  dairy: 'Milk / dairy',
  nuts: 'Tree nuts',
  peanuts: 'Peanuts',
  soy: 'Soy',
  egg: 'Egg',
  fish: 'Fish',
  shellfish: 'Shellfish',
  sesame: 'Sesame',
  mustard: 'Mustard',
};

export const ITEM_KINDS = ['dish', 'combo'] as const;
export const ItemKindSchema = z.enum(ITEM_KINDS);
export type ItemKind = z.infer<typeof ItemKindSchema>;

/**
 * pending   a new dish waiting for Ops review; customers do not see it
 * live      on the menu
 * hidden    taken off the menu by the restaurant (kept, can come back)
 * rejected  Ops refused the new dish
 * removed   deleted from the menu
 */
export const ITEM_STATUSES = ['pending', 'live', 'hidden', 'rejected', 'removed'] as const;
export const ItemStatusSchema = z.enum(ITEM_STATUSES);
export type ItemStatus = z.infer<typeof ItemStatusSchema>;
export const ITEM_STATUS_LABELS: Record<ItemStatus, string> = {
  pending: 'Waiting for review',
  live: 'On menu',
  hidden: 'Hidden',
  rejected: 'Refused',
  removed: 'Removed',
};

const DishNameSchema = z
  .string()
  .trim()
  .min(2, 'Use at least 2 letters')
  .max(80, 'Keep it under 80 letters');
const ShortNameSchema = z.string().trim().min(1, 'Add a name').max(40, 'Keep it under 40 letters');

/* ------------------------------------------------------------------ */
/* Money formatting (shared by server diffs and the web)               */
/* ------------------------------------------------------------------ */

const groupedWhole = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 });
const groupedCents = new Intl.NumberFormat('en-IN', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export const CURRENCY_SYMBOL: Record<CountryCode, string> = { IN: '₹', NP: 'Rs ' };

/** "₹1,25,000" / "Rs 1,25,000.50" — Indian / Nepali grouping, paise only when there are some. */
export function formatMoney(minor: number, country: CountryCode): string {
  const sign = minor < 0 ? '−' : '';
  const abs = Math.abs(minor);
  const text = abs % 100 === 0 ? groupedWhole.format(abs / 100) : groupedCents.format(abs / 100);
  return `${sign}${CURRENCY_SYMBOL[country]}${text}`;
}

/** "11:30 AM" from minutes after midnight. */
export function formatMinuteOfDay(minutes: number): string {
  const inDay = ((minutes % 1440) + 1440) % 1440;
  const h24 = Math.floor(inDay / 60);
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return `${h12}:${String(inDay % 60).padStart(2, '0')} ${h24 < 12 ? 'AM' : 'PM'}`;
}

/* ------------------------------------------------------------------ */
/* Building blocks                                                     */
/* ------------------------------------------------------------------ */

export const VariantSchema = z.object({
  id: z.uuid(),
  name: ShortNameSchema,
  priceMinor: MenuMoneySchema,
  isDefault: z.boolean(),
});
export type Variant = z.infer<typeof VariantSchema>;

export const VariantInputSchema = VariantSchema.extend({ id: z.uuid().optional() });
export type VariantInput = z.infer<typeof VariantInputSchema>;

export const ComboSlotSchema = z.object({
  id: z.uuid(),
  name: ShortNameSchema,
  itemIds: z.array(z.uuid()).min(1, 'Pick at least one dish').max(20),
  pick: z.number().int().min(1).max(5),
});
export type ComboSlot = z.infer<typeof ComboSlotSchema>;

export const ComboInputSchema = z.object({
  slots: z
    .array(ComboSlotSchema.extend({ id: z.uuid().optional() }))
    .min(1, 'A combo needs at least one choice group')
    .max(6),
});
export type ComboInput = z.infer<typeof ComboInputSchema>;
export type Combo = { slots: ComboSlot[] };

export const ADDON_SELECTIONS = ['one', 'many'] as const;
export const AddonSelectionSchema = z.enum(ADDON_SELECTIONS);
export type AddonSelection = z.infer<typeof AddonSelectionSchema>;

export const AddonOptionSchema = z.object({
  id: z.uuid(),
  name: z.string().trim().min(1).max(60),
  priceMinor: MenuMoneySchema,
  foodType: MenuFoodTypeSchema,
  isActive: z.boolean(),
});
export type AddonOption = z.infer<typeof AddonOptionSchema>;

const AddonGroupFieldsSchema = z.object({
  name: z.string().trim().min(2).max(60),
  selection: AddonSelectionSchema,
  minSelect: z.number().int().min(0).max(20),
  maxSelect: z.number().int().min(1).max(20),
  options: z
    .array(AddonOptionSchema.extend({ id: z.uuid().optional() }))
    .min(1, 'Add at least one option')
    .max(30),
});

function checkAddonRules(
  value: { selection?: AddonSelection; minSelect?: number; maxSelect?: number },
  ctx: z.RefinementCtx,
): void {
  if (value.minSelect !== undefined && value.maxSelect !== undefined) {
    if (value.minSelect > value.maxSelect) {
      ctx.addIssue({
        code: 'custom',
        path: ['minSelect'],
        message: 'The minimum cannot be more than the maximum',
      });
    }
  }
  if (value.selection === 'one' && value.maxSelect !== undefined && value.maxSelect !== 1) {
    ctx.addIssue({
      code: 'custom',
      path: ['maxSelect'],
      message: '“Choose one” groups allow exactly one',
    });
  }
}

export const AddonGroupInputSchema = AddonGroupFieldsSchema.superRefine(checkAddonRules);
export type AddonGroupInput = z.infer<typeof AddonGroupInputSchema>;

export const UpdateAddonGroupInputSchema = AddonGroupFieldsSchema.partial()
  .extend({ isActive: z.boolean().optional(), expectedVersion: z.number().int().min(1) })
  .superRefine(checkAddonRules);
export type UpdateAddonGroupInput = z.infer<typeof UpdateAddonGroupInputSchema>;

export const AddonGroupViewSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  selection: AddonSelectionSchema,
  minSelect: z.number().int(),
  maxSelect: z.number().int(),
  options: z.array(AddonOptionSchema),
  isActive: z.boolean(),
  usedBy: z.number().int(),
  version: z.number().int(),
});
export type AddonGroupView = z.infer<typeof AddonGroupViewSchema>;

/** ISO weekdays: 1 = Monday … 7 = Sunday. */
export const WEEKDAY_LABELS: Record<number, string> = {
  1: 'Mon',
  2: 'Tue',
  3: 'Wed',
  4: 'Thu',
  5: 'Fri',
  6: 'Sat',
  7: 'Sun',
};

const TimeSlotFieldsSchema = z.object({
  name: z.string().trim().min(2).max(40),
  days: z.array(z.number().int().min(1).max(7)).min(1, 'Pick at least one day').max(7),
  startMinute: z.number().int().min(0).max(1439),
  endMinute: z.number().int().min(0).max(1439),
});

function checkSlot(value: { startMinute?: number; endMinute?: number }, ctx: z.RefinementCtx) {
  if (
    value.startMinute !== undefined &&
    value.endMinute !== undefined &&
    value.startMinute === value.endMinute
  ) {
    ctx.addIssue({ code: 'custom', path: ['endMinute'], message: 'Start and end cannot match' });
  }
}

export const TimeSlotInputSchema = TimeSlotFieldsSchema.superRefine(checkSlot);
export type TimeSlotInput = z.infer<typeof TimeSlotInputSchema>;
export const UpdateTimeSlotInputSchema = TimeSlotFieldsSchema.partial()
  .extend({ expectedVersion: z.number().int().min(1) })
  .superRefine(checkSlot);
export type UpdateTimeSlotInput = z.infer<typeof UpdateTimeSlotInputSchema>;

export const TimeSlotViewSchema = TimeSlotFieldsSchema.extend({
  id: z.uuid(),
  usedBy: z.number().int(),
  version: z.number().int(),
});
export type TimeSlotView = z.infer<typeof TimeSlotViewSchema>;

/* ------------------------------------------------------------------ */
/* Categories                                                          */
/* ------------------------------------------------------------------ */

export const CategoryInputSchema = z.object({
  name: z.string().trim().min(2, 'Use at least 2 letters').max(60),
  description: z.string().trim().max(200).nullable().optional(),
  slotIds: z.array(z.uuid()).max(6).optional(),
  isActive: z.boolean().optional(),
});
export type CategoryInput = z.infer<typeof CategoryInputSchema>;

export const UpdateCategoryInputSchema = CategoryInputSchema.partial().extend({
  expectedVersion: z.number().int().min(1),
});
export type UpdateCategoryInput = z.infer<typeof UpdateCategoryInputSchema>;

export const CategoryViewSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  description: z.string().nullable(),
  sortOrder: z.number().int(),
  isActive: z.boolean(),
  slotIds: z.array(z.uuid()),
  itemCount: z.number().int(),
  version: z.number().int(),
});
export type CategoryView = z.infer<typeof CategoryViewSchema>;

export const ReorderInputSchema = z.object({
  categoryId: z.uuid().optional(),
  ids: z.array(z.uuid()).min(1).max(500),
});
export type ReorderInput = z.infer<typeof ReorderInputSchema>;

/* ------------------------------------------------------------------ */
/* Dishes and combos                                                   */
/* ------------------------------------------------------------------ */

const ItemFieldsSchema = z.object({
  categoryId: z.uuid(),
  kind: ItemKindSchema,
  name: DishNameSchema,
  description: z.string().trim().max(300).nullable(),
  foodType: MenuFoodTypeSchema,
  spiceLevel: SpiceLevelSchema.nullable(),
  allergens: z.array(AllergenSchema).max(ALLERGENS.length),
  portion: z.string().trim().max(40).nullable(),
  calories: z.number().int().min(0).max(5000).nullable(),
  prepMinutes: z.number().int().min(1).max(120).nullable(),
  priceMinor: MenuMoneySchema,
  packagingMinor: MenuMoneySchema,
  variants: z.array(VariantInputSchema).max(8),
  addonGroupIds: z.array(z.uuid()).max(10),
  combo: ComboInputSchema.nullable(),
  tags: z.array(z.string().trim().min(2).max(24)).max(6),
  isBestseller: z.boolean(),
  slotIds: z.array(z.uuid()).max(6),
});

function checkItemShape(
  value: {
    kind?: ItemKind;
    combo?: ComboInput | null;
    variants?: VariantInput[];
    addonGroupIds?: string[];
  },
  ctx: z.RefinementCtx,
): void {
  if (value.kind === 'combo' && value.combo === null) {
    ctx.addIssue({ code: 'custom', path: ['combo'], message: 'Add the combo choices' });
  }
  if (value.kind === 'dish' && value.combo) {
    ctx.addIssue({ code: 'custom', path: ['combo'], message: 'Only combos have choices' });
  }
  if (value.variants && value.variants.length > 0) {
    const defaults = value.variants.filter((variant) => variant.isDefault).length;
    if (defaults !== 1) {
      ctx.addIssue({
        code: 'custom',
        path: ['variants'],
        message: 'Mark exactly one size as the default',
      });
    }
    const names = new Set(value.variants.map((variant) => variant.name.toLowerCase()));
    if (names.size !== value.variants.length) {
      ctx.addIssue({ code: 'custom', path: ['variants'], message: 'Size names must differ' });
    }
  }
  if (value.addonGroupIds && new Set(value.addonGroupIds).size !== value.addonGroupIds.length) {
    ctx.addIssue({
      code: 'custom',
      path: ['addonGroupIds'],
      message: 'An add-on group is listed twice',
    });
  }
}

export const CreateItemInputSchema = ItemFieldsSchema.extend({
  kind: ItemKindSchema.default('dish'),
  description: ItemFieldsSchema.shape.description.default(null),
  spiceLevel: ItemFieldsSchema.shape.spiceLevel.default(null),
  allergens: ItemFieldsSchema.shape.allergens.default([]),
  portion: ItemFieldsSchema.shape.portion.default(null),
  calories: ItemFieldsSchema.shape.calories.default(null),
  prepMinutes: ItemFieldsSchema.shape.prepMinutes.default(null),
  packagingMinor: MenuMoneySchema.default(0),
  variants: ItemFieldsSchema.shape.variants.default([]),
  addonGroupIds: ItemFieldsSchema.shape.addonGroupIds.default([]),
  combo: ItemFieldsSchema.shape.combo.default(null),
  tags: ItemFieldsSchema.shape.tags.default([]),
  isBestseller: z.boolean().default(false),
  slotIds: ItemFieldsSchema.shape.slotIds.default([]),
  photoId: z.uuid().nullable().optional(),
}).superRefine(checkItemShape);
export type CreateItemInput = z.infer<typeof CreateItemInputSchema>;

export const UpdateItemInputSchema = ItemFieldsSchema.partial()
  .extend({
    photoId: z.uuid().nullable().optional(),
    expectedVersion: z.number().int().min(1),
    /** Why the price or name changed. Shown to the reviewer. */
    note: z.string().trim().max(200).optional(),
  })
  .superRefine(checkItemShape)
  .refine(
    (value) => Object.keys(value).some((key) => key !== 'expectedVersion' && key !== 'note'),
    'Send at least one field to change',
  );
export type UpdateItemInput = z.infer<typeof UpdateItemInputSchema>;

export const ITEM_VISIBILITY_ACTIONS = ['hide', 'show', 'remove'] as const;
export const ItemVisibilityInputSchema = z.object({
  action: z.enum(ITEM_VISIBILITY_ACTIONS),
  expectedVersion: z.number().int().min(1),
});
export type ItemVisibilityInput = z.infer<typeof ItemVisibilityInputSchema>;

export const STOCK_UNTIL = ['today', 'time', 'manual'] as const;
export const StockInputSchema = z.discriminatedUnion('state', [
  z.object({ state: z.literal('in_stock') }),
  z.object({
    state: z.literal('sold_out'),
    /** today = back at the daily reset (04:00); time = back at `at`; manual = until turned on. */
    until: z.enum(STOCK_UNTIL),
    at: z.iso.datetime().optional(),
  }),
]);
export type StockInput = z.infer<typeof StockInputSchema>;

export const UNAVAILABLE_REASONS = ['sold_out', 'outside_hours', 'hidden', 'pending'] as const;
export type UnavailableReason = (typeof UNAVAILABLE_REASONS)[number];

export const ItemStockSchema = z.object({
  soldOut: z.boolean(),
  soldOutUntil: z.string().nullable(),
  available: z.boolean(),
  reason: z.enum(UNAVAILABLE_REASONS).nullable(),
  /** When a customer can order it next, if not now. */
  nextAvailableAt: z.string().nullable(),
});
export type ItemStock = z.infer<typeof ItemStockSchema>;

export const MenuPhotoViewSchema = z.object({
  id: z.uuid(),
  smallUrl: z.string(),
  largeUrl: z.string(),
  width: z.number().int(),
  height: z.number().int(),
});
export type MenuPhotoView = z.infer<typeof MenuPhotoViewSchema>;

export const MENU_CHANGE_REASONS = [
  'new_dish',
  'price_up',
  'price_down',
  'rename',
  'food_type',
  'sizes',
  'combo',
] as const;
export const MenuChangeReasonSchema = z.enum(MENU_CHANGE_REASONS);
export type MenuChangeReason = z.infer<typeof MenuChangeReasonSchema>;
export const MENU_CHANGE_REASON_LABELS: Record<MenuChangeReason, string> = {
  new_dish: 'New dish',
  price_up: 'Price up',
  price_down: 'Price down',
  rename: 'Renamed',
  food_type: 'Veg / non-veg changed',
  sizes: 'Sizes changed',
  combo: 'Combo choices changed',
};

export const PendingChangeSchema = z.object({
  id: z.uuid(),
  reasons: z.array(MenuChangeReasonSchema),
  submittedAt: z.string(),
  dueAt: z.string(),
  /** Only the fields waiting for review, with their proposed values. */
  proposed: z.record(z.string(), z.unknown()),
});
export type PendingChange = z.infer<typeof PendingChangeSchema>;

export const MenuItemViewSchema = z.object({
  id: z.uuid(),
  categoryId: z.uuid(),
  kind: ItemKindSchema,
  name: z.string(),
  description: z.string().nullable(),
  foodType: MenuFoodTypeSchema,
  spiceLevel: SpiceLevelSchema.nullable(),
  allergens: z.array(AllergenSchema),
  portion: z.string().nullable(),
  calories: z.number().int().nullable(),
  prepMinutes: z.number().int().nullable(),
  priceMinor: z.number().int(),
  packagingMinor: z.number().int(),
  variants: z.array(VariantSchema),
  addonGroupIds: z.array(z.uuid()),
  combo: z.object({ slots: z.array(ComboSlotSchema) }).nullable(),
  tags: z.array(z.string()),
  isBestseller: z.boolean(),
  slotIds: z.array(z.uuid()),
  status: ItemStatusSchema,
  photo: MenuPhotoViewSchema.nullable(),
  stock: ItemStockSchema,
  pendingChange: PendingChangeSchema.nullable(),
  reviewNote: z.string().nullable(),
  sortOrder: z.number().int(),
  version: z.number().int(),
  updatedAt: z.string(),
});
export type MenuItemView = z.infer<typeof MenuItemViewSchema>;

/** What an edit did: fields that went live at once, and the change waiting for review. */
export const ItemSaveResultSchema = z.object({
  item: MenuItemViewSchema,
  appliedNow: z.array(z.string()),
  sentForReview: PendingChangeSchema.nullable(),
});
export type ItemSaveResult = z.infer<typeof ItemSaveResultSchema>;

/* ------------------------------------------------------------------ */
/* The menu of one outlet                                              */
/* ------------------------------------------------------------------ */

export const MenuSettingsSchema = z.object({
  reviewSlaHours: z.number(),
  instantPriceDrop: z.boolean(),
  typoSimilarityPercent: z.number(),
  packagingMaxPerItemMinor: z.number().int(),
  packagingMaxPerOrderMinor: z.number().int(),
  soldOutResetMinute: z.number().int(),
});
export type MenuSettings = z.infer<typeof MenuSettingsSchema>;

export const MenuOutletSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  restaurantId: z.uuid(),
  restaurantName: z.string(),
  status: OutletStatusSchema,
  townName: z.string(),
  country: CountryCodeSchema,
  timezone: z.string(),
  foodType: z.enum(['veg', 'non_veg', 'both']),
  coverPhoto: MenuPhotoViewSchema.nullable(),
});
export type MenuOutlet = z.infer<typeof MenuOutletSchema>;

export const MenuCountsSchema = z.object({
  items: z.number().int(),
  live: z.number().int(),
  pending: z.number().int(),
  soldOut: z.number().int(),
  withPhoto: z.number().int(),
  changesWaiting: z.number().int(),
});
export type MenuCounts = z.infer<typeof MenuCountsSchema>;

export const MenuDetailSchema = z.object({
  outlet: MenuOutletSchema,
  categories: z.array(CategoryViewSchema),
  items: z.array(MenuItemViewSchema),
  addonGroups: z.array(AddonGroupViewSchema),
  timeSlots: z.array(TimeSlotViewSchema),
  settings: MenuSettingsSchema,
  counts: MenuCountsSchema,
  canEdit: z.boolean(),
  canReview: z.boolean(),
  generatedAt: z.string(),
});
export type MenuDetail = z.infer<typeof MenuDetailSchema>;

export const MenuOutletRowSchema = z.object({
  outletId: z.uuid(),
  outletName: z.string(),
  restaurantName: z.string(),
  townName: z.string(),
  outletStatus: OutletStatusSchema,
  items: z.number().int(),
  live: z.number().int(),
  pending: z.number().int(),
  soldOut: z.number().int(),
  withPhoto: z.number().int(),
  menuUpdatedAt: z.string().nullable(),
});
export type MenuOutletRow = z.infer<typeof MenuOutletRowSchema>;

export const MenuOutletListQuerySchema = z.object({
  search: z.string().trim().max(80).optional(),
  townId: z.uuid().optional(),
  limit: z.coerce.number().int().min(1).max(1000).default(500),
});
export type MenuOutletListQuery = z.infer<typeof MenuOutletListQuerySchema>;

/** What customers would see at a given moment. */
export const MenuPreviewSchema = z.object({
  at: z.string(),
  categories: z.array(
    z.object({
      id: z.uuid(),
      name: z.string(),
      items: z.array(
        z.object({
          id: z.uuid(),
          name: z.string(),
          kind: ItemKindSchema,
          foodType: MenuFoodTypeSchema,
          priceMinor: z.number().int(),
          isBestseller: z.boolean(),
          photoUrl: z.string().nullable(),
          available: z.boolean(),
          reason: z.enum(UNAVAILABLE_REASONS).nullable(),
          nextAvailableAt: z.string().nullable(),
        }),
      ),
    }),
  ),
});
export type MenuPreview = z.infer<typeof MenuPreviewSchema>;

/* ------------------------------------------------------------------ */
/* Photos                                                              */
/* ------------------------------------------------------------------ */

export const MENU_PHOTO_PURPOSES = ['dish', 'cover'] as const;
export const MenuPhotoPurposeSchema = z.enum(MENU_PHOTO_PURPOSES);
export type MenuPhotoPurpose = z.infer<typeof MenuPhotoPurposeSchema>;

export const MENU_PHOTO_CONTENT_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const;
export const MAX_MENU_PHOTO_BYTES = 10 * 1024 * 1024;

/** Sizes we store (always WebP). Dish photos are square; covers are 16:9. */
export const MENU_PHOTO_SIZES: Record<
  MenuPhotoPurpose,
  {
    large: { width: number; height: number; maxBytes: number };
    small: { width: number; height: number; maxBytes: number };
  }
> = {
  dish: {
    large: { width: 500, height: 500, maxBytes: 30 * 1024 },
    small: { width: 200, height: 200, maxBytes: 10 * 1024 },
  },
  cover: {
    large: { width: 1200, height: 675, maxBytes: 100 * 1024 },
    small: { width: 600, height: 338, maxBytes: 35 * 1024 },
  },
};

export const StartPhotoUploadInputSchema = z.object({
  purpose: MenuPhotoPurposeSchema,
  itemId: z.uuid().optional(),
  contentType: z.enum(MENU_PHOTO_CONTENT_TYPES),
  sizeBytes: z.number().int().min(1).max(MAX_MENU_PHOTO_BYTES, 'Photos can be at most 10 MB'),
});
export type StartPhotoUploadInput = z.infer<typeof StartPhotoUploadInputSchema>;

export const UploadTargetSchema = z.object({
  id: z.uuid(),
  url: z.string(),
  method: z.literal('PUT'),
  headers: z.record(z.string(), z.string()),
  expiresAt: z.string(),
});
export type UploadTarget = z.infer<typeof UploadTargetSchema>;

/* ------------------------------------------------------------------ */
/* Ops review of menu changes                                          */
/* ------------------------------------------------------------------ */

export const MENU_CHANGE_STATUSES = [
  'pending',
  'approved',
  'rejected',
  'withdrawn',
  'superseded',
] as const;
export const MenuChangeStatusSchema = z.enum(MENU_CHANGE_STATUSES);
export type MenuChangeStatus = z.infer<typeof MenuChangeStatusSchema>;
export const MENU_CHANGE_STATUS_LABELS: Record<MenuChangeStatus, string> = {
  pending: 'Waiting',
  approved: 'Approved',
  rejected: 'Refused',
  withdrawn: 'Withdrawn',
  superseded: 'Replaced by a newer edit',
};

export const DiffLineSchema = z.object({
  field: z.string(),
  label: z.string(),
  before: z.string().nullable(),
  after: z.string().nullable(),
});
export type DiffLine = z.infer<typeof DiffLineSchema>;

export const MenuChangeRowSchema = z.object({
  id: z.uuid(),
  outletId: z.uuid(),
  outletName: z.string(),
  restaurantName: z.string(),
  townName: z.string(),
  country: CountryCodeSchema,
  itemId: z.uuid(),
  itemName: z.string(),
  isNewItem: z.boolean(),
  reasons: z.array(MenuChangeReasonSchema),
  status: MenuChangeStatusSchema,
  note: z.string().nullable(),
  submittedAt: z.string(),
  submittedBy: z.string(),
  submittedByName: z.string().nullable(),
  dueAt: z.string(),
  reviewedAt: z.string().nullable(),
  reviewedByName: z.string().nullable(),
  reviewNote: z.string().nullable(),
  diff: z.array(DiffLineSchema),
});
export type MenuChangeRow = z.infer<typeof MenuChangeRowSchema>;

export const MENU_CHANGE_VIEWS = ['pending', 'late', 'decided', 'all'] as const;
export const MenuChangeViewSchema = z.enum(MENU_CHANGE_VIEWS);
export type MenuChangeView = z.infer<typeof MenuChangeViewSchema>;

export const MenuChangeListQuerySchema = z.object({
  view: MenuChangeViewSchema.default('pending'),
  search: z.string().trim().max(80).optional(),
  townId: z.uuid().optional(),
  outletId: z.uuid().optional(),
  limit: z.coerce.number().int().min(1).max(1000).default(500),
});
export type MenuChangeListQuery = z.infer<typeof MenuChangeListQuerySchema>;

export const MenuChangeListSchema = z.object({
  rows: z.array(MenuChangeRowSchema),
  counts: z.record(MenuChangeViewSchema, z.number().int()),
});
export type MenuChangeList = z.infer<typeof MenuChangeListSchema>;

export const ReviewMenuChangeInputSchema = z.discriminatedUnion('decision', [
  z.object({ decision: z.literal('approve'), note: z.string().trim().max(300).optional() }),
  z.object({
    decision: z.literal('reject'),
    note: z.string().trim().min(3, 'Tell the restaurant why').max(300),
  }),
]);
export type ReviewMenuChangeInput = z.infer<typeof ReviewMenuChangeInputSchema>;

/* ------------------------------------------------------------------ */
/* Menu-card scanner (AI reads a photo or PDF into a draft)            */
/* ------------------------------------------------------------------ */

export const MENU_SCAN_CONTENT_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'application/pdf',
] as const;
export const MAX_MENU_SCAN_BYTES = 20 * 1024 * 1024;

export const MENU_SCAN_STATUSES = [
  'pending_upload',
  'reading',
  'ready',
  'failed',
  'applied',
  'discarded',
] as const;
export const MenuScanStatusSchema = z.enum(MENU_SCAN_STATUSES);
export type MenuScanStatus = z.infer<typeof MenuScanStatusSchema>;
export const MENU_SCAN_STATUS_LABELS: Record<MenuScanStatus, string> = {
  pending_upload: 'Uploading',
  reading: 'Reading',
  ready: 'Ready to check',
  failed: 'Could not read',
  applied: 'Added to menu',
  discarded: 'Discarded',
};

export const ReadVariantSchema = z.object({
  name: z.string().trim().min(1).max(40),
  priceMinor: MenuMoneySchema,
});
export const ReadItemSchema = z.object({
  name: z.string().trim().min(1).max(80),
  description: z.string().trim().max(300).nullable(),
  priceMinor: MenuMoneySchema.nullable(),
  foodType: MenuFoodTypeSchema.nullable(),
  variants: z.array(ReadVariantSchema).max(8),
});
export type ReadItem = z.infer<typeof ReadItemSchema>;
export const ReadCategorySchema = z.object({
  name: z.string().trim().min(1).max(60),
  items: z.array(ReadItemSchema).max(200),
});
export type ReadCategory = z.infer<typeof ReadCategorySchema>;
export const MenuReadingSchema = z.object({
  categories: z.array(ReadCategorySchema).max(60),
  currency: z.string().max(3).nullable(),
  notes: z.string().max(500).nullable(),
});
export type MenuReading = z.infer<typeof MenuReadingSchema>;

export const StartScanUploadInputSchema = z.object({
  fileName: z.string().trim().min(1).max(120),
  contentType: z.enum(MENU_SCAN_CONTENT_TYPES),
  sizeBytes: z.number().int().min(1).max(MAX_MENU_SCAN_BYTES, 'Files can be at most 20 MB'),
});
export type StartScanUploadInput = z.infer<typeof StartScanUploadInputSchema>;

export const MenuScanViewSchema = z.object({
  id: z.uuid(),
  status: MenuScanStatusSchema,
  fileName: z.string().nullable(),
  contentType: z.string(),
  provider: z.string().nullable(),
  model: z.string().nullable(),
  reading: MenuReadingSchema.nullable(),
  itemCount: z.number().int(),
  appliedCount: z.number().int(),
  error: z.string().nullable(),
  createdAt: z.string(),
  createdByName: z.string().nullable(),
  completedAt: z.string().nullable(),
  appliedAt: z.string().nullable(),
});
export type MenuScanView = z.infer<typeof MenuScanViewSchema>;

export const ApplyScanInputSchema = z.object({
  categories: z
    .array(
      z.object({
        name: z.string().trim().min(2).max(60),
        existingCategoryId: z.uuid().nullable().optional(),
        items: z
          .array(
            z.object({
              name: DishNameSchema,
              description: z.string().trim().max(300).nullable(),
              priceMinor: MenuMoneySchema,
              foodType: MenuFoodTypeSchema,
              variants: z.array(ReadVariantSchema).max(8),
            }),
          )
          .min(1)
          .max(200),
      }),
    )
    .min(1, 'Pick at least one dish to add')
    .max(60),
});
export type ApplyScanInput = z.infer<typeof ApplyScanInputSchema>;

export const ApplyScanResultSchema = z.object({
  scan: MenuScanViewSchema,
  categoriesCreated: z.number().int(),
  itemsCreated: z.number().int(),
  sentForReview: z.boolean(),
});
export type ApplyScanResult = z.infer<typeof ApplyScanResultSchema>;

/* ------------------------------------------------------------------ */
/* Price parity (app price must not be above the menu card)            */
/* ------------------------------------------------------------------ */

export const PARITY_SOURCES = ['ops', 'mi', 'rider', 'customer'] as const;
export const ParitySourceSchema = z.enum(PARITY_SOURCES);
export type ParitySource = z.infer<typeof ParitySourceSchema>;
export const PARITY_SOURCE_LABELS: Record<ParitySource, string> = {
  ops: 'Ops',
  mi: 'Market initialer',
  rider: 'Rider',
  customer: 'Customer bill',
};

export const PARITY_STATUSES = [
  'pending_upload',
  'reading',
  'to_decide',
  'clear',
  'confirmed',
  'dismissed',
  'failed',
] as const;
export const ParityStatusSchema = z.enum(PARITY_STATUSES);
export type ParityStatus = z.infer<typeof ParityStatusSchema>;
export const PARITY_STATUS_LABELS: Record<ParityStatus, string> = {
  pending_upload: 'Uploading',
  reading: 'Reading',
  to_decide: 'To decide',
  clear: 'No mismatch',
  confirmed: 'Mismatch confirmed',
  dismissed: 'Dismissed',
  failed: 'Could not read',
};

export const PARITY_VERDICTS = ['ok', 'app_higher', 'app_lower', 'not_found'] as const;
export type ParityVerdict = (typeof PARITY_VERDICTS)[number];

export const ParityMatchSchema = z.object({
  cardName: z.string(),
  cardVariant: z.string().nullable(),
  cardPriceMinor: z.number().int(),
  itemId: z.uuid().nullable(),
  itemName: z.string().nullable(),
  variantName: z.string().nullable(),
  appPriceMinor: z.number().int().nullable(),
  diffMinor: z.number().int().nullable(),
  diffPercent: z.number().nullable(),
  similarity: z.number().int(),
  verdict: z.enum(PARITY_VERDICTS),
});
export type ParityMatch = z.infer<typeof ParityMatchSchema>;

export const ParitySummarySchema = z.object({
  read: z.number().int(),
  ok: z.number().int(),
  higher: z.number().int(),
  lower: z.number().int(),
  notFound: z.number().int(),
});
export type ParitySummary = z.infer<typeof ParitySummarySchema>;

/** Step 1 warn (fix within the deadline) → step 2 ranking drop → step 3 switched off. */
export const PARITY_STEP_LABELS: Record<number, string> = {
  1: 'Warning — fix within the deadline',
  2: 'Ranking drop',
  3: 'Switched off',
};

export const ParityDecisionSchema = z.object({
  decision: z.enum(['confirm', 'dismiss']),
  note: z.string(),
  decidedAt: z.string(),
  decidedByName: z.string().nullable(),
  strikeStep: z.number().int().nullable(),
  feeMinor: z.number().int().nullable(),
  fixBy: z.string().nullable(),
});
export type ParityDecision = z.infer<typeof ParityDecisionSchema>;

export const ParityCheckViewSchema = z.object({
  id: z.uuid(),
  outletId: z.uuid(),
  outletName: z.string(),
  restaurantName: z.string(),
  townName: z.string(),
  country: CountryCodeSchema,
  source: ParitySourceSchema,
  status: ParityStatusSchema,
  matches: z.array(ParityMatchSchema),
  summary: ParitySummarySchema,
  decision: ParityDecisionSchema.nullable(),
  error: z.string().nullable(),
  createdAt: z.string(),
  createdByName: z.string().nullable(),
  completedAt: z.string().nullable(),
});
export type ParityCheckView = z.infer<typeof ParityCheckViewSchema>;

export const StartParityUploadInputSchema = z.object({
  source: ParitySourceSchema,
  contentType: z.enum(MENU_SCAN_CONTENT_TYPES),
  sizeBytes: z.number().int().min(1).max(MAX_MENU_SCAN_BYTES, 'Files can be at most 20 MB'),
});
export type StartParityUploadInput = z.infer<typeof StartParityUploadInputSchema>;

export const PARITY_VIEWS = ['to_decide', 'confirmed', 'all'] as const;
export const ParityViewSchema = z.enum(PARITY_VIEWS);
export type ParityView = z.infer<typeof ParityViewSchema>;

export const ParityListQuerySchema = z.object({
  view: ParityViewSchema.default('to_decide'),
  outletId: z.uuid().optional(),
  townId: z.uuid().optional(),
  limit: z.coerce.number().int().min(1).max(500).default(200),
});
export type ParityListQuery = z.infer<typeof ParityListQuerySchema>;

export const ParityListSchema = z.object({
  rows: z.array(ParityCheckViewSchema),
  counts: z.record(ParityViewSchema, z.number().int()),
});
export type ParityList = z.infer<typeof ParityListSchema>;

export const ParityDecisionInputSchema = z.object({
  decision: z.enum(['confirm', 'dismiss']),
  note: z.string().trim().min(3, 'Add a short note').max(300),
});
export type ParityDecisionInput = z.infer<typeof ParityDecisionInputSchema>;

export const MenuSweepResultSchema = z.object({
  backInStock: z.number().int(),
  overdueChanges: z.number().int(),
  stuckJobs: z.number().int(),
});
export type MenuSweepResult = z.infer<typeof MenuSweepResultSchema>;
